/**
 * MaskFormatter
 *
 * Generic character-by-character mask formatter.
 * Accepts raw input, strips disallowed characters, caps at maxLength,
 * and provides ghost-mask display parts (typed chars + remaining ghost chars).
 *
 * Usage:
 *   const formatter = new MaskFormatter({ maxLength: 11 });
 *   formatter.format('03abc12');           // '0312'
 *   formatter.getMaskParts('031');         // { typed: '031', ghost: 'XXXXXXXX' }
 */
export class MaskFormatter {
  /**
   * @param {object} options
   * @param {number}  options.maxLength   — maximum number of accepted characters
   * @param {RegExp}  [options.charRegex] — pattern an accepted character must match, default /\d/
   * @param {string}  [options.ghostChar] — character used for remaining positions, default 'X'
   */
  constructor({ maxLength, charRegex = /\d/, ghostChar = 'X' }) {
    this.maxLength = maxLength;
    this.charRegex = charRegex;
    this.ghostChar = ghostChar;
  }

  /**
   * Returns only the accepted characters from value, capped at maxLength.
   *
   * @param {string} value
   * @returns {string}
   */
  format(value) {
    return String(value || '')
      .split('')
      .filter(c => this.charRegex.test(c))
      .slice(0, this.maxLength)
      .join('');
  }

  /**
   * Alias of format — exists for interface parity with MaskFormatterImpl.
   *
   * @param {string} value
   * @returns {string}
   */
  unformat(value) {
    return this.format(value);
  }

  /**
   * Returns the two overlay parts needed to render the ghost mask.
   *   typed — the accepted chars already entered
   *   ghost — remaining ghost characters (ghostChar repeated)
   *
   * @param {string} value
   * @returns {{ typed: string, ghost: string }}
   */
  getMaskParts(value) {
    const typed = this.format(value);
    const ghost = this.ghostChar.repeat(Math.max(0, this.maxLength - typed.length));
    return { typed, ghost };
  }
}

/**
 * Factory — mirrors the MaskFormatterFactory pattern.
 *
 * @param {object} options
 * @returns {MaskFormatter}
 */
export const createMaskFormatter = options => new MaskFormatter(options);
